import React from 'react';
import VentureRegister from './VentureRegister';
import Icon from './icons';
import * as api from './api';
import { blankCo, officeOf, projectPlan } from './types';
import { changeOffice } from './office';
import { placeholderLpSum } from './coterms';
import { bondOf, withdrawOf } from './terms';
import { pruneAfterRegRemove } from './coRemove';
import type { Project, RegState, Co } from './types';

interface Props {
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
}

/** 등록정보 후입력/수정 — 등록 화면과 동일 UI(검색·자동완성), 자동 저장 */
export default function RegTab({ p, onPatch, toast }: Props) {
  const reg: RegState = {
    org: p.org || '', investPlan: projectPlan(p), withdraw: withdrawOf(p), bond: bondOf(p), office: officeOf(p), addr: p.addr || '', total: p.total || '', per: p.per || '', dur: p.dur || '',
    gp: p.gp || '', gpTel: p.gpTel || '', gpEmail: p.gpEmail || '',
    cos: p.cos && p.cos.length ? p.cos : [blankCo()],
  };

  const field = (k: keyof RegState, v: string) => {
    if (k === 'office') { changeOffice(p, v === 'atv' ? 'atv' : 'roum', onPatch, toast); return; }   // 로고·기존 포털까지 같이
    onPatch({ [k]: v } as Partial<Project>);
  };
  const setCos = (cos: Co[]) => {
    const patch: Partial<Project> = { cos };
    const c0 = cos[0];
    if (c0) { patch.fp = c0.fpName || ''; patch.fpCo = c0.fpCo || p.fpCo || '한화생명'; }
    onPatch(patch);
  };
  const onCo = (i: number, f: keyof Co, v: string) => {
    setCos(reg.cos.map((c, j) => (j === i ? ({ ...c, [f]: v } as Co) : c)));
  };
  const onCoPatch = (i: number, patch: Partial<Co>) => {
    setCos(reg.cos.map((c, j) => (j === i ? { ...c, ...patch } : c)));
    if (patch.companyId) {
      api.linkCompany(p.id, patch.companyId).then(() => toast('업무관리 업체와 연동됨')).catch(() => undefined);
    }
  };
  const onCoPatches = (ps: Array<Partial<Co> | null>) => setCos(reg.cos.map((c, j) => (ps[j] ? { ...c, ...ps[j] } : c)));
  const addCo = () => setCos([...reg.cos, blankCo()]);
  // 회사 지우기 — 자료수집 쪽 짝 카드도 함께(안 지우면 회사별 진행 · 8~15단계에 그대로 남았다 · 2026-11-02)
  const removeCo = (i: number) => {
    if (reg.cos.length <= 1) return;
    const next = reg.cos.filter((_, j) => j !== i);
    const extra = pruneAfterRegRemove(p, reg.cos[i], next);
    const c0 = next[0];
    onPatch({ cos: next, ...extra, ...(c0 ? { fp: c0.fpName || '', fpCo: c0.fpCo || p.fpCo || '한화생명' } : {}) });
  };

  return (
    <>
      <div className="alert blue" style={{ marginBottom: 10 }}>
        <Icon n="info" />
        <div><b>뒤늦게 입력해도 됩니다</b><span> 소개자(FP)·투자법인·조합 정보를 여기서 언제든 수정하세요. 자동 저장되고, 1단계 자료수집에는 빈 값만 채워지는 방식으로 안전하게 반영됩니다.</span></div>
      </div>
      <VentureRegister mode="edit" reg={reg} onField={field} onCo={onCo} onCoPatch={onCoPatch} onCoPatches={onCoPatches} reserved={placeholderLpSum(p.master && p.master.lps)} onAddCo={addCo} onRemoveCo={removeCo} />
    </>
  );
}
